import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { sanitizeText, sanitizeUrl } from '../utils/security';

interface NavbarLink {
  src: string;
  alt: string;
  url: string;
  // Optional decorative brand mark rendered before the main image (used for
  // the CardRevive mark + wordmark lockup; aria-hidden, alt lives on src).
  markSrc?: string;
  width?: number;
  height?: number;
  markWidth?: number;
  markHeight?: number;
  // Adds rel="sponsored" so partner placements are honestly labelled.
  sponsored?: boolean;
}

interface FooterProps {
  links?: NavbarLink[];
  onPromotionClick?: () => void;
}

// Honest, human-visible cross-promotion to CardRevive (Bill's Australian
// trading card restoration and grading business). rel="sponsored" declares
// the commercial relationship to search engines; noopener/noreferrer is the
// standard safety pairing for target="_blank" links.
const CARDREVIVE_FOOTER_URL = 'https://www.cardrevive.com.au/?utm_source=tcgcalendar&utm_medium=referral&utm_campaign=footer';

const FooterContainer = styled.footer`
  background-color: var(--surface);
  border-top: 1px solid var(--border);
  padding: 0.45rem 1rem;
  text-align: center;
  color: var(--text-secondary);
  font-size: 0.875rem;
  flex-shrink: 0; /* Prevent footer from shrinking */
  margin-top: auto; /* Push footer to bottom */
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    margin-top: 2rem; /* Add more space above footer on mobile */
    border-top: 2px solid var(--border); /* Make border more visible on mobile */
  }
`;

const FooterRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1.25rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const Copyright = styled.div`
  white-space: nowrap;
`;

const FooterLink = styled(Link)`
  color: var(--primary);
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
    color: var(--primary);
  }
`;

// Same visual treatment as FooterLink, but a plain anchor: FooterLink wraps
// react-router's <Link>, which is for internal client-side routes, not an
// external absolute URL opened in a new tab.
const FooterExternalLink = styled.a`
  color: var(--primary);
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
    color: var(--primary);
  }
`;

const PoweredBySection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin: 0;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 0.4rem;
  }
`;

const PoweredBy = styled.span`
  color: var(--text-secondary);
  font-size: 0.875rem;
  font-weight: 500;
`;

// Fixed width/height so every partner pill (CardRevive's mark + wordmark
// lockup, The Card Hub's single logo) renders as an identical-sized box; each
// logo is centred and scaled to fit via object-fit: contain rather than
// dictating the pill's size from its own content.
const SponsorLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  width: 6rem;
  height: 2.25rem;
  box-sizing: border-box;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.4rem 0.5rem;
  overflow: hidden;

  img {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
    transition: transform 0.3s ease;

    &:hover {
      transform: scale(1.05);
    }
  }
`;

const PromotionButton = styled.button`
  background: color-mix(in srgb, var(--primary) 12%, var(--surface));
  color: var(--text);
  border: 1px solid var(--border);
  padding: 0.42rem 0.9rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: color-mix(in srgb, var(--primary) 18%, var(--surface));
    border-color: var(--primary);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(233, 179, 57, 0.2);
  }
`;

const PromotionReach = styled.span`
  color: var(--text-secondary);
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;

  strong {
    color: var(--primary);
  }
`;

const Footer: React.FC<FooterProps> = ({ links = [], onPromotionClick }) => {
  return (
    <FooterContainer>
      <FooterRow>
        <Copyright>
          © 2026 TCG Calendar Australia
        </Copyright>
        <FooterLink to="/release-notes">
          Release Notes
        </FooterLink>
        <FooterLink to="/sets">
          Set Releases
        </FooterLink>
        <FooterLink to="/privacy">
          Privacy
        </FooterLink>
        <FooterLink to="/contact">
          Contact
        </FooterLink>
        <FooterLink to="/partners">
          Partners
        </FooterLink>
        <FooterExternalLink
          href={CARDREVIVE_FOOTER_URL}
          target="_blank"
          rel="sponsored noopener noreferrer"
        >
          Card grading and restoration by CardRevive
        </FooterExternalLink>
        {links.length > 0 && (
          <PoweredBySection>
            <PoweredBy>Powered by</PoweredBy>
            {links.map((link, index) => {
              const safeUrl = sanitizeUrl(link.url);
              return safeUrl ? (
                <SponsorLink
                  key={index}
                  href={safeUrl}
                  target="_blank"
                  rel={link.sponsored ? 'sponsored noopener noreferrer' : 'noopener noreferrer'}
                  aria-label={`Visit ${sanitizeText(link.alt)}`}
                >
                  {link.markSrc && (
                    <img
                      src={link.markSrc}
                      alt=""
                      aria-hidden="true"
                      loading="lazy"
                      width={link.markWidth}
                      height={link.markHeight}
                    />
                  )}
                  <img
                    src={link.src}
                    alt={sanitizeText(link.alt)}
                    loading="lazy"
                    width={link.width}
                    height={link.height}
                  />
                </SponsorLink>
              ) : null;
            })}
          </PoweredBySection>
        )}
        {onPromotionClick && (
          <>
            <PromotionReach><strong>12,000+</strong> monthly visitors</PromotionReach>
            <PromotionButton onClick={onPromotionClick}>
              Promote Your Event
            </PromotionButton>
          </>
        )}
      </FooterRow>
    </FooterContainer>
  );
};

export default Footer;
